import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { api } from '../../api/client';
import type { LiveTvProviderSummary } from '../../api/types';
import { useApi } from '../../api/useApi';
import AsyncSection from '../AsyncSection';
import ChannelScanPanel from './ChannelScanPanel';
import { useProviderName } from './lineup';

/// Where live television comes from: every live TV plugin, what its tuners are doing, where its
/// devices, playlists or accounts are set up, and the channel scan for the ones that tune. One
/// place for all of them, so no plugin needs a menu entry of its own.
export default function ReceptionSourcesPanel({ providers }: { providers: LiveTvProviderSummary[] | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const providerName = useProviderName(providers);

  const sources = useApi(['livetv', 'sources'], api.liveTvSources);

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        {t('livetvChannels.sources.intro')}
      </Typography>

      <AsyncSection loading={sources.loading} error={sources.error} onRetry={sources.reload}>
        {(sources.data ?? []).length === 0 ? (
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/plugins')}>
                {t('nav.plugins')}
              </Button>
            }
          >
            {t('livetvChannels.sources.none')}
          </Alert>
        ) : (
          <Stack spacing={2}>
            {sources.data?.map((source) => (
              <Card key={source.providerId} variant="outlined">
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                        {providerName(source.providerId, source.displayNameKey)}
                      </Typography>
                      {source.configurable && (
                        <Button size="small" onClick={() => navigate(`/plugins/${source.providerId}`)}>
                          {t('livetvChannels.sources.configure')}
                        </Button>
                      )}
                    </Stack>

                    {source.error && (
                      <Alert severity="error">{t('livetvChannels.sources.failed', { detail: source.error })}</Alert>
                    )}

                    {!source.error && source.tuners.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {t('livetvChannels.sources.noTuners')}
                      </Typography>
                    )}

                    {source.tuners.map((tuner) => (
                      <Stack
                        key={tuner.tunerId}
                        direction="row"
                        spacing={1}
                        useFlexGap
                        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                      >
                        <Typography variant="body2" sx={{ flexGrow: 1, overflowWrap: 'anywhere' }}>
                          {tuner.name}
                        </Typography>
                        {tuner.error ? (
                          <Chip size="small" color="error" label={tuner.error} />
                        ) : (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={tuner.activeStreams > 0 ? 'primary' : 'default'}
                            label={t('livetvChannels.sources.streams', {
                              active: tuner.activeStreams,
                              max: tuner.maxStreams,
                            })}
                          />
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </AsyncSection>

      {providers?.some((provider) => provider.canScan) && (
        <Stack spacing={2}>
          <Typography variant="h3" sx={{ fontSize: 20 }}>
            {t('livetvChannels.tab.scan')}
          </Typography>

          <ChannelScanPanel providers={providers} />
        </Stack>
      )}
    </Stack>
  );
}
